import type { JSONContent } from "@tiptap/core";
import { normalizeButtonLinkHref } from "./button-link-url";
import { convertToEmbedUrl } from "./video-url-converter";

export const VIDEO_ASPECT_RATIOS = ["16:9", "4:3", "21:9", "9:16"] as const;

export type VideoAspectRatioValueT = (typeof VIDEO_ASPECT_RATIOS)[number];

export type TiptapContentSecurityIssueT = {
  path: Array<string | number>;
  message: string;
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function normalizeVideoAspectRatio(
  value: unknown
): VideoAspectRatioValueT {
  return VIDEO_ASPECT_RATIOS.includes(value as VideoAspectRatioValueT)
    ? (value as VideoAspectRatioValueT)
    : "16:9";
}

function collectNodeIssues(
  node: JSONContent,
  path: Array<string | number>,
  issues: TiptapContentSecurityIssueT[]
): void {
  if (node.type === "videoEmbed") {
    const src = node.attrs?.src;
    if (isNonEmptyString(src) && !convertToEmbedUrl(src)) {
      issues.push({
        path: [...path, "attrs", "src"],
        message: "Die Video-URL stammt nicht von einem unterstützten Anbieter.",
      });
    }

    const aspectRatio = node.attrs?.aspectRatio;
    if (
      aspectRatio !== undefined &&
      !VIDEO_ASPECT_RATIOS.includes(aspectRatio as VideoAspectRatioValueT)
    ) {
      issues.push({
        path: [...path, "attrs", "aspectRatio"],
        message: "Das Video-Seitenverhältnis ist ungültig.",
      });
    }
  }

  if (node.type === "buttonLink") {
    const href = node.attrs?.href;
    if (isNonEmptyString(href) && !normalizeButtonLinkHref(href)) {
      issues.push({
        path: [...path, "attrs", "href"],
        message: "Der Button-Link verwendet keine erlaubte URL.",
      });
    }
  }

  node.content?.forEach((child, index) => {
    collectNodeIssues(child, [...path, "content", index], issues);
  });
}

export function getTiptapContentSecurityIssues(
  content: JSONContent
): TiptapContentSecurityIssueT[] {
  const issues: TiptapContentSecurityIssueT[] = [];
  collectNodeIssues(content, [], issues);
  return issues;
}

function normalizeNode(node: JSONContent): JSONContent {
  const normalizedContent = node.content?.map(normalizeNode);
  let normalizedAttrs = node.attrs ? { ...node.attrs } : undefined;

  if (node.type === "videoEmbed") {
    const src = node.attrs?.src;
    const canonicalSrc = isNonEmptyString(src) ? convertToEmbedUrl(src) : null;
    normalizedAttrs = {
      ...normalizedAttrs,
      src: canonicalSrc ?? "",
      aspectRatio: normalizeVideoAspectRatio(node.attrs?.aspectRatio),
    };
  }

  if (node.type === "buttonLink") {
    normalizedAttrs = {
      ...normalizedAttrs,
      href: normalizeButtonLinkHref(node.attrs?.href) ?? "#",
    };
  }

  return {
    ...node,
    ...(normalizedAttrs ? { attrs: normalizedAttrs } : {}),
    ...(normalizedContent ? { content: normalizedContent } : {}),
  };
}

/**
 * Canonicalizes safe URLs and neutralizes unsafe legacy attributes without
 * mutating the stored JSON object.
 */
export function normalizeTiptapContentAttributes(
  content: JSONContent
): JSONContent {
  return normalizeNode(content);
}

function galleryHasImage(images: unknown): boolean {
  if (!Array.isArray(images)) return false;

  return images.some((image) => {
    if (!image || typeof image !== "object") return false;
    const record = image as Record<string, unknown>;
    return isNonEmptyString(record.src) || isNonEmptyString(record.url);
  });
}

export function hasMeaningfulTiptapContent(node: JSONContent): boolean {
  if (node.type === "text" && isNonEmptyString(node.text)) {
    return true;
  }

  if (node.type === "image" && isNonEmptyString(node.attrs?.src)) {
    return true;
  }

  if (node.type === "gallery" && galleryHasImage(node.attrs?.images)) {
    return true;
  }

  if (node.type === "videoEmbed" && isNonEmptyString(node.attrs?.src)) {
    return convertToEmbedUrl(node.attrs.src) !== null;
  }

  if (node.type === "buttonLink") {
    const href = normalizeButtonLinkHref(node.attrs?.href);
    return isNonEmptyString(node.attrs?.text) && href !== null && href !== "#";
  }

  return node.content?.some(hasMeaningfulTiptapContent) ?? false;
}
